import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCallback } from 'react';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient, queryClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import {
  createEventMessagesQueryKey,
  createExperienceEventMessagesQueryKey,
} from '@/utils/queryKeys';
import type {
  DeleteEventMessageMutation,
  DeleteEventMessageMutationVariables,
} from './useDeleteEventMessage.generated';

const query = gql`
  mutation DeleteEventMessage($input: DeleteEventMessageInput!) {
    deleteEventMessage(input: $input)
  }
`;

export const useDeleteEventMessage = (eventId: string) => {
  const { experience } = usePageExperienceContext();

  const { mutate, ...rest } = useMutation<
    DeleteEventMessageMutation,
    QueryError,
    DeleteEventMessageMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        DeleteEventMessageMutation,
        DeleteEventMessageMutationVariables
      >(query, variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createEventMessagesQueryKey(experience.id, eventId),
      });
      queryClient.invalidateQueries({
        queryKey: createExperienceEventMessagesQueryKey(experience.id),
      });
    },
  });

  const deleteEventMessage = useCallback(
    (
      eventMessageId: string,
      options?: MutateOptions<
        DeleteEventMessageMutation,
        QueryError,
        DeleteEventMessageMutationVariables
      >
    ) =>
      mutate(
        {
          input: {
            experienceId: experience.id,
            eventMessageId,
          },
        },
        options
      ),
    [experience.id, mutate]
  );

  return { deleteEventMessage, ...rest };
};
